// window.__NEON__ debug/test API (only installed with ?debug in the URL).
// Every method is defensive: it returns an error string instead of throwing.

import type Phaser from 'phaser';
import { SCENES } from '../config/constants';
import type { RouteSceneData } from '../core/contracts';
import { bus } from '../core/events';
import { getRun, startRun } from '../core/run';
import { session } from '../core/session';
import type { EnemyId, MechId, NodeType, OrbType, Rarity } from '../core/types';
import { ROOMS } from '../data/rooms';
import { makeItem } from '../systems/loot/items';
import { ENEMY_ART_SCALE, RIGS, frameMeta, type EnemyAnim } from '../systems/enemies/rig';

const safe =
  <A extends unknown[], R>(fn: (...a: A) => R) =>
  (...a: A): R | string => {
    try {
      return fn(...a);
    } catch (err) {
      return `error: ${String((err as Error)?.message ?? err)}`;
    }
  };

export function installDebug(game: Phaser.Game): void {
  let godMode = false;
  const ctx = () => session.ctx;
  const sm = () => game.scene;
  const active = () => sm().getScenes(true).map((s) => s.sys.settings.key);

  const api = {
    get ready() {
      const a = active();
      return a.length > 0 && !a.includes(SCENES.BOOT);
    },
    game,
    scenes: active,
    ctx,
    run: getRun,

    startRun: safe((opts: { mech?: MechId; seed?: number; node?: NodeType } = {}) => {
      for (const k of [SCENES.GAME, SCENES.UI, SCENES.ROUTE, SCENES.REWARD, SCENES.STATION, SCENES.PAUSE, SCENES.MENU, SCENES.HANGAR, SCENES.RESULTS]) {
        if (sm().isActive(k) || sm().isPaused(k)) sm().stop(k);
      }
      const run = startRun({ mechId: opts.mech ?? 'volt', seed: opts.seed ?? 1 });
      let nodeId = run.nodeId;
      if (opts.node && opts.node !== 'start') {
        const found = Object.values(run.route.nodes).find((n) => n.type === opts.node);
        if (found) nodeId = found.id;
        else {
          const room = ROOMS.find((r) => r.type === opts.node);
          const start = run.route.nodes[run.route.startId];
          start.type = opts.node;
          if (room) start.roomId = room.id;
        }
      }
      sm().start(SCENES.GAME, { nodeId });
      return nodeId;
    }),

    give: safe((defId: string, rarity: Rarity = 'standard') => {
      const run = getRun();
      if (!run) return 'no run';
      const item = makeItem(defId, rarity, run.rng.loot);
      if (!item) return `unknown item ${defId}`;
      run.inventory.equip(item);
      run.inventory.changed();
      return item.uid;
    }),
    spawn: safe((id: EnemyId, x?: number, y?: number, elite = false) => {
      const c = ctx()!;
      const p = c.player;
      c.enemies.spawn(id, x ?? p.x + 400, y ?? p.feetY, { elite: elite ? ['shielded'] : undefined });
      return 'ok';
    }),
    killAll: safe(() => {
      ctx()!.enemies.killAll();
      return 'ok';
    }),
    god: safe((on: boolean = true) => {
      godMode = on;
      return godMode;
    }),
    heal: safe(() => {
      const p = ctx()!.player;
      p.hp = p.stats.maxHp;
      p.shield = p.stats.maxShield;
      return p.hp;
    }),
    setHp: safe((n: number) => {
      ctx()!.player.hp = n;
      return n;
    }),
    setScrap: safe((n: number) => {
      const run = getRun()!;
      run.addScrap(n - run.scrap);
      return run.scrap;
    }),
    openOrb: safe((type: OrbType = 'arsenal') => {
      const c = ctx()!;
      c.loot.spawnOrb(type, c.player.x + 150, c.player.y - 20, { source: 'debug' });
      return 'ok';
    }),
    clearRoom: safe(() => {
      const c = ctx()!;
      c.enemies.killAll();
      return c.world.state;
    }),
    exitRoom: safe(() => {
      bus.emit('room:exitRequested', {});
      return 'ok';
    }),
    pickRoute: safe((index: number = 0) => {
      const scene = sm().getScene(SCENES.ROUTE);
      const data = scene?.sys.settings.data as RouteSceneData | undefined;
      const run = getRun();
      if (!data || !run) return 'route scene not open';
      const next = run.nextNodes()[index] ?? run.nextNodes()[0];
      data.onPick(next.id);
      return next.id;
    }),
    fps: () => Math.round(game.loop.actualFps),
    /** Enemy rig + one frame's attachment points (art checks). */
    enemyRig: (id: EnemyId, anim: EnemyAnim = 'idle', i = 0) => ({ rig: RIGS[id], meta: frameMeta(id, anim, i), scale: ENEMY_ART_SCALE }),
    state: safe(() => {
      const c = ctx();
      const run = getRun();
      const p = c?.player;
      const w = p?.activeWeapon();
      return {
        scenes: active(),
        node: run?.node.type,
        roomId: c?.world.roomId,
        roomState: c?.world.state,
        player: p && { x: Math.round(p.x), y: Math.round(p.y), hp: Math.round(p.hp), shield: Math.round(p.shield), state: p.state, weapon: w?.def.id, heat: w ? +w.heat.toFixed(2) : null, onGround: p.onGround },
        enemies: c?.enemies.list.filter((e) => e.kind !== 'prop').map((e) => ({ id: e.defId, x: Math.round(e.x), y: Math.round(e.y), hp: Math.round(e.hp), spawning: e.spawning })),
        projectiles: c?.projectiles.activeCount,
        boss: c?.bosses.active ? { hp: Math.round(c.bosses.active.hp), phase: c.bosses.active.phase } : null,
        scrap: run?.scrap,
        inventory: run && { weapons: run.inventory.weapons.map((x) => x?.defId ?? null), equipment: run.inventory.visualIds(), mutations: run.inventory.mutations.map((m) => m.defId) },
        synergies: c?.effects.activeSynergies,
        fps: Math.round(game.loop.actualFps),
      };
    }),
  };

  // God mode keeps the player topped up every frame.
  game.events.on('step', () => {
    if (!godMode) return;
    const p = session.ctx?.player;
    if (p && p.alive) {
      p.hp = p.stats.maxHp;
      p.shield = p.stats.maxShield;
    }
  });

  (window as unknown as { __NEON__: typeof api }).__NEON__ = api;
}
